import { ConfiguredJob } from "common/state/rtcJobsApi";
import { FeatureCollection, LineString, Polygon } from "geojson";
import { Layer, Source } from "react-map-gl";
import { lineString } from "@turf/helpers";
import { makeTaskRenderer, TaskWith } from "../TaskLayers";
import {
  mapboxGetColorForTaskState,
  mapboxGetOpacityForTaskState,
} from "common/components/map/layers/mapboxHelpers.task";
import { TASK_BASE_PROPS, TaskFeatureProperties } from "common/utils/rtcJobs";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import buffer from "@turf/buffer";
import distance from "@turf/distance";
import React, { useMemo } from "react";

// TODO: remove this and use the one from planting when it is available ==
export const DEFAULT_ROW_WIDTH = 6.7056;
// ==

function taskBoxesForLaserWeedTasks(
  configuredJob: ConfiguredJob,
  tasks: TaskWith<"laserWeed">[]
): {
  progressPaths: FeatureCollection<Polygon, TaskFeatureProperties>;
  outlines: FeatureCollection<Polygon, TaskFeatureProperties>;
} {
  let rowWidthMeters = DEFAULT_ROW_WIDTH;

  if (configuredJob.laserWeed) {
    rowWidthMeters = configuredJob.laserWeed.rowSpacingM;
  } else {
    console.error(
      "rowBoxesForLaserWeedRowObjective: laser weed row objective on non-laser weed task?"
    );
  }

  const progressPaths: FeatureCollection<Polygon, TaskFeatureProperties> = {
    type: "FeatureCollection",
    features: tasks.flatMap((t) => {
      let start = t.startLocation;
      let end = t.endLocation;
      const properties = {
        ...TASK_BASE_PROPS,
        task: t,
      };
      const p0 = t.laserWeed.path?.points[0];
      const p1 = t.laserWeed.path?.points[1];
      if (start && p0 && p1) {
        const d0 = distance([start.lng, start.lat], [p0.lng, p0.lat], {
          units: "meters",
        });
        const d1 = distance([start.lng, start.lat], [p1.lng, p1.lat], {
          units: "meters",
        });
        end = end ?? (d0 <= d1 ? p1 : p0);
      } else {
        start = start ?? p0;
        end = end ?? p1;
      }
      if (!start || !end) {
        return [];
      }

      const bufferedLine = buffer<LineString>(
        lineString(
          t.progressPath?.points.map((pt) => [pt.lng, pt.lat]) ?? [
            [start.lng, start.lat],
            [end.lng, end.lat],
          ]
        ),
        rowWidthMeters / 4,
        { units: "meters" }
      );

      return [{ ...bufferedLine, properties }];
    }),
  };
  const outlines: FeatureCollection<Polygon, TaskFeatureProperties> = {
    type: "FeatureCollection",
    features: tasks.flatMap((t) => {
      const start = t.startLocation;
      const end = t.endLocation;
      const pathPoints = t.laserWeed.path?.points;
      if (!pathPoints && (!start || !end)) {
        return [];
      }
      const properties = {
        ...TASK_BASE_PROPS,
        task: t,
      };
      const outlineBufferedLine = buffer<LineString>(
        lineString(
          pathPoints?.map((pt) => [pt.lng, pt.lat]) ?? [
            [start!.lng, start!.lat],
            [end!.lng, end!.lat],
          ]
        ),
        rowWidthMeters / 4,
        { units: "meters" }
      );

      return [{ ...outlineBufferedLine, properties }];
    }),
  };

  return { progressPaths, outlines };
}

enum LayerIds {
  FILL = "lwt-fill",
  OUTLINE_FILL = "lwt-outline-fill",
  OUTLINE = "lwt-outline",
}

export const renderLaserWeedRowTask = makeTaskRenderer({
  detailsField: "laserWeed",
  Render: ({ configuredJob, tasks, extraLayerProps, beforeId }) => {
    const { progressPaths, outlines } = useMemo(() => {
      return taskBoxesForLaserWeedTasks(configuredJob, tasks);
    }, [configuredJob, tasks]);
    return (
      <>
        <Source data={progressPaths} type="geojson">
          <Layer
            id={LayerIds.FILL}
            beforeId={LayerIds.OUTLINE}
            type="fill"
            paint={{
              "fill-color": ["coalesce", mapboxGetColorForTaskState, "#FFF"],
              "fill-opacity": mapboxGetOpacityForTaskState,
            }}
            {...extraLayerProps}
          />
        </Source>
        <Source data={outlines} type="geojson">
          <Layer
            id={LayerIds.OUTLINE}
            beforeId={beforeId}
            type="line"
            paint={{
              "line-color": theme.colors.black,
            }}
            {...extraLayerProps}
          />
        </Source>
      </>
    );
  },
  interactiveLayerIds: [LayerIds.FILL],
  imagesToLoad: [],
});
